import React from 'react';
import { Pagination as BootstrapPagination, ButtonGroup, Button } from 'react-bootstrap';
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import TablePaginationFooter from './TablePaginationFooter';

export interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
  has_more_pages?: boolean;
}

export interface PaginationProps {
  meta: PaginationMeta | null;
  onPageChange: (page: number) => void;
  className?: string;
  testId?: string;
  showInfo?: boolean;
  showPerPageSelector?: boolean;
  perPageOptions?: number[];
  onPerPageChange?: (perPage: number) => void;
  maxVisiblePages?: number;
}

export function Pagination({
  meta,
  onPageChange,
  className = '',
  testId = 'pagination',
  showInfo = true,
  showPerPageSelector = false,
  perPageOptions = [10, 25, 50, 100],
  onPerPageChange,
  maxVisiblePages = 5
}: PaginationProps) {
  if (!meta || meta.last_page <= 1) {
    return null;
  }

  const { current_page, last_page, per_page, total, from, to } = meta;

  // Calculate visible page numbers
  const getVisiblePages = () => {
    const pages: (number | string)[] = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);
    
    let startPage = Math.max(1, current_page - halfVisible);
    let endPage = Math.min(last_page, current_page + halfVisible);
    
    // Adjust if we're near the beginning or end
    if (endPage - startPage + 1 < maxVisiblePages) {
      if (startPage === 1) {
        endPage = Math.min(last_page, startPage + maxVisiblePages - 1);
      } else {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
    }
    
    // Add first page and ellipsis if needed
    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) {
        pages.push('...');
      }
    }
    
    // Add visible pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    // Add ellipsis and last page if needed
    if (endPage < last_page) {
      if (endPage < last_page - 1) {
        pages.push('...');
      }
      pages.push(last_page);
    }
    
    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <TablePaginationFooter
      className={className}
      testId={testId}
      info={showInfo ? `Showing ${from} to ${to} of ${total} entries` : undefined}
    >
      <div className="d-flex align-items-center gap-3 flex-wrap">
        {/* Per Page Selector — hidden on mobile */}
        {showPerPageSelector && onPerPageChange && (
          <div className="d-none d-sm-flex align-items-center gap-2">
            <span className="text-muted small">Per page:</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={per_page}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onPerPageChange(Number(e.target.value))}
              data-testid={`${testId}-per-page-selector`}
            >
              {perPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Pagination Buttons */}
        <BootstrapPagination className="mb-0 ntcog-pagination-bar" data-testid={`${testId}-controls`}>
          {/* First Page */}
          <BootstrapPagination.First
            onClick={() => onPageChange(1)}
            disabled={current_page === 1}
            data-testid={`${testId}-first-button`}
          >
            <FiChevronsLeft />
          </BootstrapPagination.First>

          {/* Previous Page */}
          <BootstrapPagination.Prev
            onClick={() => onPageChange(current_page - 1)}
            disabled={current_page === 1}
            data-testid={`${testId}-prev-button`}
          >
            <FiChevronLeft />
          </BootstrapPagination.Prev>

          {/* Page Numbers */}
          {visiblePages.map((page, index) => (
            <BootstrapPagination.Item
              key={index}
              active={page === current_page}
              onClick={() => typeof page === 'number' && onPageChange(page)}
              disabled={page === '...'}
              data-testid={`${testId}-page-${page}`}
            >
              {page}
            </BootstrapPagination.Item>
          ))}

          {/* Next Page */}
          <BootstrapPagination.Next
            onClick={() => onPageChange(current_page + 1)}
            disabled={current_page === last_page}
            data-testid={`${testId}-next-button`}
          >
            <FiChevronRight />
          </BootstrapPagination.Next>

          {/* Last Page */}
          <BootstrapPagination.Last
            onClick={() => onPageChange(last_page)}
            disabled={current_page === last_page}
            data-testid={`${testId}-last-button`}
          >
            <FiChevronsRight />
          </BootstrapPagination.Last>
        </BootstrapPagination>
      </div>
    </TablePaginationFooter>
  );
}

// Compact pagination for smaller spaces
export function CompactPagination({
  meta,
  onPageChange,
  className = '',
  testId = 'compact-pagination'
}: Omit<PaginationProps, 'showInfo' | 'showPerPageSelector' | 'onPerPageChange' | 'perPageOptions' | 'maxVisiblePages'>) {
  if (!meta || meta.last_page <= 1) {
    return null;
  }

  const { current_page, last_page } = meta;

  return (
    <div className={`d-flex justify-content-center ${className}`} data-testid={testId}>
      <ButtonGroup>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => onPageChange(current_page - 1)}
          disabled={current_page === 1}
          data-testid={`${testId}-prev-button`}
        >
          <FiChevronLeft />
        </Button>
        
        <Button
          variant="outline-primary"
          size="sm"
          disabled
          data-testid={`${testId}-page-info`}
        >
          {current_page} of {last_page}
        </Button>
        
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => onPageChange(current_page + 1)}
          disabled={current_page === last_page}
          data-testid={`${testId}-next-button`}
        >
          <FiChevronRight />
        </Button>
      </ButtonGroup>
    </div>
  );
}
